import React from "react";
import { Info, Save } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./HomebuyerFinancialInfo.css";
import { useAuth0 } from "@auth0/auth0-react";
import { updateDossierFinancialInfo } from "./homebuyerDossierApi";

const EMPTY_BORROWER_FINANCIAL_INFO = {
  net_income: "",
  listing_in_cpk: "",
  monthly_expenses: "",
  current_rent: "",
  employment_status: "",
  contract_type: "",
  employment_contracts_count: "",
  politically_exposed_person: "",
  notes: "",
};

const EMPTY_DOSSIER_FINANCIAL_INFO = {
  reuse_simulation_file_name: "",
  total_loan_amount: "",
  renovation_loan_amount: "",
  equity_amount: "",
  property_price: "",
  tenure_years: 9,
  monthly_cost_percent: 40,
  estimated_monthly_payment: "",
  notary_of_choice: "",
  repayment_bank_account: "",
  loan_disbursement_bank_account: "",
  notes: "",
  purchase_loan_amount: "",
  estimated_renovation_budget: "",
  renovation_estimate_method: "",
  renovation_estimate_source: "PROPERTY_INFORMATION",
  epc_points_reduced: "",
  energy_improvement_percent: "",
};

const BORROWER_MONEY_FIELDS = new Set([
  "net_income",
  "monthly_expenses",
  "current_rent",
]);

const DOSSIER_MONEY_FIELDS = new Set([
  "renovation_loan_amount",
  "equity_amount",
  "property_price",
  "purchase_loan_amount",
]);

const REQUIRED_BORROWER_FIELDS = [
  "net_income",
  "listing_in_cpk",
  "monthly_expenses",
  "employment_status",
  "contract_type",
  "employment_contracts_count",
  "politically_exposed_person",
];

const REQUIRED_DOSSIER_FIELDS = [
  "property_price",
  "equity_amount",
  "tenure_years",
  "monthly_cost_percent",
  "repayment_bank_account",
  "loan_disbursement_bank_account",
];

function stripToDigits(value = "") {
  return String(value).replace(/[^\d]/g, "");
}

function normalizeMoneyValue(value = "") {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value).trim();
  if (!text) return "";

  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return stripToDigits(text);

  return String(Math.round(parsed));
}

function formatThousands(value = "") {
  const normalized = normalizeMoneyValue(value);
  if (!normalized) return "";
  return Number(normalized).toLocaleString("en-US");
}

function safeTrim(value) {
  return String(value ?? "").trim();
}

function validateIBAN(iban) {
  if (!iban) return false;

  const cleaned = iban.replace(/\s+/g, "").toUpperCase();

  if (!/^[A-Z0-9]+$/.test(cleaned)) return false;
  if (cleaned.length < 15 || cleaned.length > 34) return false;

  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);

  const numeric = rearranged
    .split("")
    .map((ch) => {
      if (/[A-Z]/.test(ch)) return ch.charCodeAt(0) - 55;
      return ch;
    })
    .join("");

  let remainder = numeric;

  while (remainder.length > 2) {
    const block = remainder.slice(0, 9);
    remainder = String(Number(block) % 97) + remainder.slice(block.length);
  }

  return Number(remainder) % 97 === 1;
}

function formatIBAN(value = "") {
  return String(value)
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

function createBorrowerFinancial(role = "PRIMARY") {
  return {
    id: role === "PRIMARY" ? "primary" : "co_applicant",
    role,
    personal_info: {},
    financial_info: { ...EMPTY_BORROWER_FINANCIAL_INFO },
    documents: {},
    extracted_data: {},
  };
}

function isRequiredField(fieldName, scope = "borrower") {
  if (scope === "dossier") return REQUIRED_DOSSIER_FIELDS.includes(fieldName);
  return REQUIRED_BORROWER_FIELDS.includes(fieldName);
}

export default function HomebuyerFinancialInfo() {
  const { t } = useTranslation("homebuyer-financial-info");

  const { getAccessTokenSilently } = useAuth0();

  const {
    currentStepKey,
    handleStepValidityChange,
    dossierDraft,
    openSaveModal,
    setBorrowersDraft,
    updateDraftSection,
    updateDraftMetaFields,
    registerStepSubmitHandler,
    dossierId,
  } = useOutletContext();

  const activeDraft = dossierDraft || {};
  const activeDossierId = dossierId || activeDraft?.dossier_id || null;

  const FIELD_TOOLTIPS = React.useMemo(
    () => ({
      reuse_simulation_file_name: t("tooltips.reuse_simulation_file_name"),
      net_income: t("tooltips.net_income"),
      listing_in_cpk: t("tooltips.listing_in_cpk"),
      monthly_expenses: t("tooltips.monthly_expenses"),
      current_rent: t("tooltips.current_rent"),
      employment_status: t("tooltips.employment_status"),
      contract_type: t("tooltips.contract_type"),
      employment_contracts_count: t("tooltips.employment_contracts_count"),
      politically_exposed_person: t("tooltips.politically_exposed_person"),
      total_loan_amount: t("tooltips.total_loan_amount"),
      renovation_loan_amount: t("tooltips.renovation_loan_amount"),
      equity_amount: t("tooltips.equity_amount"),
      property_price: t("tooltips.property_price"),
      tenure_years: t("tooltips.tenure_years"),
      monthly_cost_percent: t("tooltips.monthly_cost_percent"),
      notary_of_choice: t("tooltips.notary_of_choice"),
      repayment_bank_account: t("tooltips.repayment_bank_account"),
      loan_disbursement_bank_account: t(
        "tooltips.loan_disbursement_bank_account",
      ),
      notes: t("tooltips.notes"),
      estimated_renovation_budget: t("tooltips.estimated_renovation_budget"),
      renovation_estimate_method: t("tooltips.renovation_estimate_method"),
      epc_points_reduced: t("tooltips.epc_points_reduced"),
      energy_improvement_percent: t("tooltips.energy_improvement_percent"),
    }),
    [t],
  );

  const [borrowers, setBorrowers] = React.useState(() => {
    const draftBorrowers = dossierDraft?.borrowers || [];

    if (draftBorrowers.length > 0) {
      return draftBorrowers.map((borrower) => ({
        ...borrower,
        financial_info: {
          ...EMPTY_BORROWER_FINANCIAL_INFO,
          ...(borrower.financial_info || {}),
        },
      }));
    }

    return [createBorrowerFinancial("PRIMARY")];
  });

  const [dossierFinancialInfo, setDossierFinancialInfo] = React.useState(
    () => ({
      ...EMPTY_DOSSIER_FINANCIAL_INFO,
      ...(dossierDraft?.dossier_financial_info || {}),
    }),
  );

  const [fieldErrors, setFieldErrors] = React.useState({});

  const draftBorrowersKey = React.useMemo(
    () => JSON.stringify(dossierDraft?.borrowers || []),
    [dossierDraft?.borrowers],
  );

  React.useEffect(() => {
    const draftBorrowers = dossierDraft?.borrowers || [];

    const normalizedBorrowers =
      draftBorrowers.length > 0
        ? draftBorrowers.map((borrower) => ({
            ...borrower,
            financial_info: {
              ...EMPTY_BORROWER_FINANCIAL_INFO,
              ...(borrower.financial_info || {}),
            },
          }))
        : [createBorrowerFinancial("PRIMARY")];

    setBorrowers((prev) => {
      const same = JSON.stringify(prev) === JSON.stringify(normalizedBorrowers);

      return same ? prev : normalizedBorrowers;
    });
  }, [draftBorrowersKey]);

  React.useEffect(() => {
    setDossierFinancialInfo({
      ...EMPTY_DOSSIER_FINANCIAL_INFO,
      ...(dossierDraft?.dossier_financial_info || {}),
    });
  }, [dossierDraft?.dossier_id]);

  React.useEffect(() => {
    setBorrowersDraft(borrowers);
  }, [borrowers, setBorrowersDraft]);

  React.useEffect(() => {
    updateDraftSection("dossier_financial_info", dossierFinancialInfo);
  }, [dossierFinancialInfo, updateDraftSection]);

  const primaryBorrower =
    borrowers.find((b) => b.role === "PRIMARY") || borrowers[0];

  const coBorrower = borrowers.find((b) => b.role === "CO_APPLICANT") || null;

  const primaryFinancial =
    primaryBorrower?.financial_info || EMPTY_BORROWER_FINANCIAL_INFO;

  const coFinancial =
    coBorrower?.financial_info || EMPTY_BORROWER_FINANCIAL_INFO;

  const hasCoApplicant = borrowers?.some((b) => b.role === "CO_APPLICANT");

  const propertyPrice = Number(dossierFinancialInfo.property_price || 0);
  const equityAmount = Number(dossierFinancialInfo.equity_amount || 0);
  const autoRenovationBudget = Number(
    dossierFinancialInfo.estimated_renovation_budget || 0,
  );

  const renovationLoanAmount = Number(
    dossierFinancialInfo.renovation_loan_amount || autoRenovationBudget || 0,
  );

  const purchaseLoanAmount = Math.max(propertyPrice - equityAmount, 0);
  const totalLoanAmount = purchaseLoanAmount + renovationLoanAmount;

  const totalNetIncome =
    Number(primaryFinancial.net_income || 0) +
    Number(coFinancial.net_income || 0);

  const estimatedMonthlyPayment =
    dossierFinancialInfo.tenure_years && totalLoanAmount
      ? totalLoanAmount / (Number(dossierFinancialInfo.tenure_years) * 12)
      : 0;

  const monthlyCostPercent =
    totalNetIncome && estimatedMonthlyPayment
      ? Math.round((estimatedMonthlyPayment / totalNetIncome) * 100)
      : null;

  function updateBorrowerFinancial(role, name, value) {
    let nextValue = value;

    if (BORROWER_MONEY_FIELDS.has(name)) {
      nextValue = stripToDigits(value);
    }

    if (name === "employment_contracts_count") {
      nextValue = value.replace(/[^\d]/g, "");
    }

    setBorrowers((prev) =>
      prev.map((borrower) =>
        borrower.role === role
          ? {
              ...borrower,
              financial_info: {
                ...EMPTY_BORROWER_FINANCIAL_INFO,
                ...(borrower.financial_info || {}),
                [name]: nextValue,
              },
            }
          : borrower,
      ),
    );
  }

  function handleDossierFinancialChange(e) {
    const { name, value } = e.target;
    let nextValue = value;

    if (DOSSIER_MONEY_FIELDS.has(name)) {
      nextValue = stripToDigits(value);
    }

    setDossierFinancialInfo((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  }

  function handleRangeChange(name, value) {
    setDossierFinancialInfo((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  }

  function handleFakeFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setDossierFinancialInfo((prev) => ({
      ...prev,
      reuse_simulation_file_name: file.name,
    }));
  }

  function clearSimulationFile() {
    setDossierFinancialInfo((prev) => ({
      ...prev,
      reuse_simulation_file_name: "",
    }));
  }

  React.useEffect(() => {
    async function handleStepSubmit() {
      if (!activeDossierId) {
        return { ok: false };
      }

      try {
        const accessToken = await getAccessTokenSilently();

        const result = await updateDossierFinancialInfo({
          accessToken,
          dossierId: activeDossierId,
          borrowers,
          dossierFinancialInfo,
        });

        updateDraftMetaFields({
          dossier_id: result.dossier_id,
          dossier_code: result.dossier_code || activeDraft.dossier_code || null,
          borrowers: result.borrowers || borrowers,
          dossier_financial_info:
            result.dossier_financial_info || dossierFinancialInfo,
        });

        return {
          ok: true,
          updatedDraft: {
            ...activeDraft,
            dossier_id: result.dossier_id,
            dossier_code:
              result.dossier_code || activeDraft.dossier_code || null,
            borrowers: result.borrowers || borrowers,
            dossier_financial_info:
              result.dossier_financial_info || dossierFinancialInfo,
          },
        };
      } catch (err) {
        console.error("Could not save financial information:", err);
        return {
          ok: false,
          error: err.message || "Could not save financial information.",
        };
      }
    }

    registerStepSubmitHandler(currentStepKey, handleStepSubmit);

    return () => {
      registerStepSubmitHandler(currentStepKey, null);
    };
  }, [
    activeDossierId,
    activeDraft,
    borrowers,
    dossierFinancialInfo,
    currentStepKey,
    getAccessTokenSilently,
    registerStepSubmitHandler,
    updateDraftMetaFields,
  ]);

  // Property Information may calculate/refresh the estimate after Financial
  // Information was saved. Synchronize both budget and requested loan, not
  // just the display-only budget. Avoid overwriting a manual loan override.
  const incomingFinancial = dossierDraft?.dossier_financial_info || {};
  const incomingBudget = incomingFinancial.estimated_renovation_budget;
  const incomingLoan = incomingFinancial.renovation_loan_amount;
  const incomingMethod = incomingFinancial.renovation_estimate_method;
  const incomingSource = incomingFinancial.renovation_estimate_source;

  React.useEffect(() => {
    if (
      incomingBudget === null ||
      incomingBudget === undefined ||
      incomingBudget === ""
    ) {
      return;
    }
    setDossierFinancialInfo((prev) => {
      const nextBudget = String(incomingBudget);
      const oldBudget = String(prev.estimated_renovation_budget ?? "");
      const oldLoan = String(prev.renovation_loan_amount ?? "");
      const returnedLoan =
        incomingLoan !== null &&
        incomingLoan !== undefined &&
        incomingLoan !== ""
          ? String(incomingLoan)
          : null;
      const nextLoan =
        returnedLoan ??
        (!oldLoan || oldLoan === oldBudget ? nextBudget : oldLoan);
      const updated = {
        ...prev,
        estimated_renovation_budget: nextBudget,
        renovation_loan_amount: nextLoan,
        renovation_estimate_source: incomingSource || "PROPERTY_INFORMATION",
        renovation_estimate_method:
          incomingMethod || prev.renovation_estimate_method,
      };
      if (
        updated.estimated_renovation_budget ===
          prev.estimated_renovation_budget &&
        updated.renovation_loan_amount === prev.renovation_loan_amount &&
        updated.renovation_estimate_source ===
          prev.renovation_estimate_source &&
        updated.renovation_estimate_method === prev.renovation_estimate_method
      ) {
        return prev;
      }
      return updated;
    });
  }, [incomingBudget, incomingLoan, incomingMethod, incomingSource]);

  function validateBorrowerFinancial(values, prefix) {
    const errors = {};

    if (!safeTrim(values.net_income)) {
      errors[`${prefix}.net_income`] = t("validation.required");
    }

    if (!values.listing_in_cpk) {
      errors[`${prefix}.listing_in_cpk`] = t("validation.required");
    }

    if (!safeTrim(values.monthly_expenses)) {
      errors[`${prefix}.monthly_expenses`] = t("validation.required");
    }

    if (!values.employment_status) {
      errors[`${prefix}.employment_status`] = t("validation.required");
    }

    if (!values.contract_type) {
      errors[`${prefix}.contract_type`] = t("validation.required");
    }

    if (!safeTrim(values.employment_contracts_count)) {
      errors[`${prefix}.employment_contracts_count`] = t("validation.required");
    }

    if (!values.politically_exposed_person) {
      errors[`${prefix}.politically_exposed_person`] = t("validation.required");
    }

    return errors;
  }

  function validateDossierFinancial(values) {
    const errors = {};

    if (!safeTrim(values.equity_amount)) {
      errors.equity_amount = t("validation.required");
    }

    if (!safeTrim(values.property_price)) {
      errors.property_price = t("validation.required");
    }

    if (
      values.tenure_years === "" ||
      values.tenure_years === null ||
      values.tenure_years === undefined
    ) {
      errors.tenure_years = t("validation.required");
    }

    if (
      values.monthly_cost_percent === "" ||
      values.monthly_cost_percent === null ||
      values.monthly_cost_percent === undefined
    ) {
      errors.monthly_cost_percent = t("validation.required");
    }

    // if (!values.notary_of_choice) {
    //   errors.notary_of_choice = t("validation.required");
    // }

    if (!safeTrim(values.repayment_bank_account)) {
      errors.repayment_bank_account = t("validation.required");
    } else if (!validateIBAN(values.repayment_bank_account)) {
      errors.repayment_bank_account = "Invalid IBAN format";
    }

    if (!safeTrim(values.loan_disbursement_bank_account)) {
      errors.loan_disbursement_bank_account = t("validation.required");
    } else if (!validateIBAN(values.loan_disbursement_bank_account)) {
      errors.loan_disbursement_bank_account = "Invalid IBAN format";
    }

    if (equityAmount > propertyPrice) {
      errors.equity_amount =
        "Own contribution cannot be greater than property price";
    }

    if (propertyPrice <= 0) {
      errors.property_price = "Property price must be greater than 0";
    }

    if (renovationLoanAmount < 0) {
      errors.renovation_loan_amount =
        "Renovation loan amount cannot be negative";
    }

    return errors;
  }

  React.useEffect(() => {
    const primaryErrors = validateBorrowerFinancial(
      primaryFinancial,
      "primary",
    );

    const coErrors = coBorrower
      ? validateBorrowerFinancial(coFinancial, "coApplicant")
      : {};

    const dossierErrors = validateDossierFinancial(dossierFinancialInfo);

    const errors = {
      ...primaryErrors,
      ...coErrors,
      ...dossierErrors,
    };

    setFieldErrors(errors);

    handleStepValidityChange(
      currentStepKey,
      Object.keys(errors).length === 0,
      errors,
      {
        borrowers,
        dossier_financial_info: dossierFinancialInfo,
      },
    );
  }, [
    borrowers,
    dossierFinancialInfo,
    currentStepKey,
    handleStepValidityChange,
    primaryFinancial,
    coFinancial,
    coBorrower,
    t,
  ]);

  React.useEffect(() => {
    setDossierFinancialInfo((prev) => ({
      ...prev,
      purchase_loan_amount: purchaseLoanAmount
        ? String(purchaseLoanAmount)
        : "",
      total_loan_amount: totalLoanAmount ? String(totalLoanAmount) : "",
    }));
  }, [purchaseLoanAmount, totalLoanAmount]);

  React.useEffect(() => {
    if (monthlyCostPercent !== null) {
      setDossierFinancialInfo((prev) => ({
        ...prev,
        monthly_cost_percent: monthlyCostPercent,
        estimated_monthly_payment: Math.round(estimatedMonthlyPayment),
      }));
    }
  }, [monthlyCostPercent, estimatedMonthlyPayment]);

  function renderLabel(label, fieldName, scope = "borrower") {
    const required = isRequiredField(fieldName, scope);

    return (
      <label className="hb-fin-field-label-row">
        <span>
          {label}
          {required ? <span className="hb-required-asterisk"> *</span> : null}
        </span>

        <span className="hb-fin-tooltip-wrap" tabIndex={0}>
          <Info size={14} strokeWidth={2.2} />
          <span className="hb-fin-tooltip">{FIELD_TOOLTIPS[fieldName]}</span>
        </span>
      </label>
    );
  }

  function renderError(fieldName) {
    const message = fieldErrors[fieldName];
    if (!message || message === t("validation.required")) return null;
    return <span className="hb-fin-field-error" role="alert">{message}</span>;
  }

  function renderBorrowerCard(title, role, values, prefix) {
    return (
      <>
        <div className="hb-fin-card">
          <h2 className="hb-fin-card__title">{title}</h2>

          <div className="hb-fin-form-grid">
            <div className="hb-fin-field hb-fin-field--with-tooltip">
              {renderLabel(t("fields.net_income"), "net_income")}
              <div className="hb-fin-money-input">
                <input
                  name="net_income"
                  value={formatThousands(values.net_income)}
                  onChange={(e) =>
                    updateBorrowerFinancial(role, "net_income", e.target.value)
                  }
                  placeholder={t("placeholders.net_income")}
                  inputMode="numeric"
                />
                <span className="hb-fin-money-suffix">€</span>
              </div>
              {renderError(`${prefix}.net_income`)}
            </div>

            <div className="hb-fin-field hb-fin-field--with-tooltip">
              {renderLabel(t("fields.listing_in_cpk"), "listing_in_cpk")}
              <select
                name="listing_in_cpk"
                value={values.listing_in_cpk}
                onChange={(e) =>
                  updateBorrowerFinancial(
                    role,
                    "listing_in_cpk",
                    e.target.value,
                  )
                }
              >
                <option value="">{t("placeholders.select")}</option>
                <option value="NO">{t("options.cpk.no")}</option>
                <option value="YES_ACTIVE">{t("options.cpk.yesActive")}</option>
                <option value="YES_HISTORICAL">
                  {t("options.cpk.yesHistorical")}
                </option>
                <option value="UNKNOWN">{t("options.cpk.unknown")}</option>
              </select>
              {renderError(`${prefix}.listing_in_cpk`)}
            </div>

            <div className="hb-fin-field hb-fin-field--with-tooltip">
              {renderLabel(t("fields.monthly_expenses"), "monthly_expenses")}
              <div className="hb-fin-money-input">
                <input
                  name="monthly_expenses"
                  value={formatThousands(values.monthly_expenses)}
                  onChange={(e) =>
                    updateBorrowerFinancial(
                      role,
                      "monthly_expenses",
                      e.target.value,
                    )
                  }
                  placeholder={t("placeholders.monthly_expenses")}
                  inputMode="numeric"
                />
                <span className="hb-fin-money-suffix">€</span>
              </div>
              {renderError(`${prefix}.monthly_expenses`)}
            </div>

            <div className="hb-fin-field hb-fin-field--with-tooltip">
              {renderLabel(t("fields.current_rent"), "current_rent")}
              <div className="hb-fin-money-input">
                <input
                  name="current_rent"
                  value={formatThousands(values.current_rent)}
                  onChange={(e) =>
                    updateBorrowerFinancial(
                      role,
                      "current_rent",
                      e.target.value,
                    )
                  }
                  placeholder={t("placeholders.current_rent")}
                  inputMode="numeric"
                />
                <span className="hb-fin-money-suffix">€</span>
              </div>
            </div>

            <div className="hb-fin-field hb-fin-field--with-tooltip">
              {renderLabel(t("fields.employment_status"), "employment_status")}
              <select
                name="employment_status"
                value={values.employment_status}
                onChange={(e) =>
                  updateBorrowerFinancial(
                    role,
                    "employment_status",
                    e.target.value,
                  )
                }
              >
                <option value="">{t("placeholders.select")}</option>
                <option value="FULL_TIME">
                  {t("options.employment.fullTime")}
                </option>
                <option value="PART_TIME">
                  {t("options.employment.partTime")}
                </option>
                <option value="SELF_EMPLOYED">
                  {t("options.employment.selfEmployed")}
                </option>
                <option value="UNEMPLOYED">
                  {t("options.employment.unemployed")}
                </option>
                <option value="STUDENT">
                  {t("options.employment.student")}
                </option>
                <option value="RETIRED">
                  {t("options.employment.retired")}
                </option>
                <option value="OTHER">{t("options.employment.other")}</option>
              </select>
              {renderError(`${prefix}.employment_status`)}
            </div>

            <div className="hb-fin-field hb-fin-field--with-tooltip">
              {renderLabel(t("fields.contract_type"), "contract_type")}
              <select
                name="contract_type"
                value={values.contract_type}
                onChange={(e) =>
                  updateBorrowerFinancial(role, "contract_type", e.target.value)
                }
              >
                <option value="">{t("placeholders.select")}</option>
                <option value="PERMANENT">
                  {t("options.contract.permanent")}
                </option>
                <option value="FIXED_TERM">
                  {t("options.contract.fixedTerm")}
                </option>
                <option value="FREELANCE">
                  {t("options.contract.freelance")}
                </option>
                <option value="INTERN">{t("options.contract.intern")}</option>
                <option value="OTHER">{t("options.contract.other")}</option>
              </select>
              {renderError(`${prefix}.contract_type`)}
            </div>

            <div className="hb-fin-field hb-fin-field--with-tooltip">
              {renderLabel(
                t("fields.employment_contracts_count"),
                "employment_contracts_count",
              )}
              <input
                name="employment_contracts_count"
                value={values.employment_contracts_count}
                onChange={(e) =>
                  updateBorrowerFinancial(
                    role,
                    "employment_contracts_count",
                    e.target.value,
                  )
                }
                placeholder={t("placeholders.employment_contracts_count")}
              />
              {renderError(`${prefix}.employment_contracts_count`)}
            </div>

            <div className="hb-fin-field hb-fin-field--with-tooltip hb-fin-field--tall-label">
              {renderLabel(
                t("fields.politically_exposed_person"),
                "politically_exposed_person",
              )}
              <select
                name="politically_exposed_person"
                value={values.politically_exposed_person}
                onChange={(e) =>
                  updateBorrowerFinancial(
                    role,
                    "politically_exposed_person",
                    e.target.value,
                  )
                }
              >
                <option value="">{t("placeholders.select")}</option>
                <option value="NO">{t("options.pep.no")}</option>
                <option value="YES_PEP">{t("options.pep.yesPep")}</option>
                <option value="YES_FAMILY_MEMBER">
                  {t("options.pep.yesFamilyMember")}
                </option>
                <option value="YES_CLOSE_ASSOCIATE">
                  {t("options.pep.yesCloseAssociate")}
                </option>
                <option value="YES_FORMER_PEP">
                  {t("options.pep.yesFormerPep")}
                </option>
              </select>
              {renderError(`${prefix}.politically_exposed_person`)}
            </div>
          </div>
        </div>

        {!hasCoApplicant && (
          <div className="hb-fin-card hb-fin-card-full hb-fin-card-full-width">
            <h2 className="hb-fin-card__title">{t("sections.compliance")}</h2>

            <div className="hb-fin-form-grid hb-fin-form-grid--compliance">
              <div className="hb-fin-field hb-fin-field--with-tooltip">
                {renderLabel(
                  t("fields.repayment_bank_account"),
                  "repayment_bank_account",
                  "dossier",
                )}
                <input
                  name="repayment_bank_account"
                  value={formatIBAN(
                    dossierFinancialInfo.repayment_bank_account,
                  )}
                  onChange={(e) => {
                    const raw = e.target.value
                      .replace(/\s+/g, "")
                      .toUpperCase();

                    setDossierFinancialInfo((prev) => ({
                      ...prev,
                      repayment_bank_account: raw,
                    }));
                  }}
                  placeholder={t("placeholders.repayment_bank_account")}
                />
                {renderError("repayment_bank_account")}
              </div>

              <div className="hb-fin-field hb-fin-field--with-tooltip">
                {renderLabel(
                  t("fields.loan_disbursement_bank_account"),
                  "loan_disbursement_bank_account",
                  "dossier",
                )}
                <input
                  name="loan_disbursement_bank_account"
                  value={formatIBAN(
                    dossierFinancialInfo.loan_disbursement_bank_account,
                  )}
                  onChange={(e) => {
                    const raw = e.target.value
                      .replace(/\s+/g, "")
                      .toUpperCase();

                    setDossierFinancialInfo((prev) => ({
                      ...prev,
                      loan_disbursement_bank_account: raw,
                    }));
                  }}
                  placeholder={t("placeholders.loan_disbursement_bank_account")}
                />
                {renderError("loan_disbursement_bank_account")}
              </div>

              {/* <div className="hb-fin-field hb-fin-field--with-tooltip">
                {renderLabel(
                  t("fields.notary_of_choice"),
                  "notary_of_choice",
                  "dossier",
                )}
                <select
                  name="notary_of_choice"
                  value={dossierFinancialInfo.notary_of_choice}
                  onChange={handleDossierFinancialChange}
                >
                  <option value="">{t("placeholders.select")}</option>
                  <option value="NOTARY_1">
                    {t("options.notary.default1")}
                  </option>
                  <option value="NOTARY_2">
                    {t("options.notary.default2")}
                  </option>
                  <option value="NOTARY_3">
                    {t("options.notary.default3")}
                  </option>
                </select>
                {renderError("notary_of_choice")}
              </div> */}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="hb-fin-page">
      <div className="hb-step-page__header">
        <h1 className="hb-step-page__title">{t("title")}</h1>

        <button
          type="button"
          className="hb-app-flow__save-btn"
          onClick={openSaveModal}
        >
          <Save size={16} />
          <span>{t("buttons.save")}</span>
        </button>
      </div>

      <div className="hb-fin-page__panel">
        {/* <div className="hb-fin-card hb-fin-card--reuse">
          <div className="hb-fin-import">
            <div className="hb-fin-import__content">
              <div className="hb-fin-import__text">
                <h2 className="hb-fin-card__title">{t("sections.reuse")}</h2>
                <p className="hb-fin-import__description">
                  {t("reuse.description")}
                </p>

                <div className="hb-fin-import__tags">
                  <span className="hb-fin-import__tag">.xlsx</span>
                  <span className="hb-fin-import__tag">.xls</span>
                  <span className="hb-fin-import__tag">.csv</span>
                </div>
              </div>

              <label className="hb-fin-upload-card">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFakeFileSelect}
                  hidden
                />

                <div className="hb-fin-upload-card__icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>

                <div className="hb-fin-upload-card__text">
                  <strong>{t("reuse.uploadTitle")}</strong>
                  <span>{t("reuse.uploadSubtitle")}</span>
                </div>
              </label>
            </div>

            <div className="hb-fin-import__footer">
              {dossierFinancialInfo.reuse_simulation_file_name ? (
                <div className="hb-fin-upload-file">
                  <div className="hb-fin-upload-file__meta">
                    <span className="hb-fin-upload-file__label">
                      {t("reuse.selectedFile")}
                    </span>
                    <strong>
                      {dossierFinancialInfo.reuse_simulation_file_name}
                    </strong>
                  </div>

                  <button
                    type="button"
                    className="hb-fin-upload-file__remove"
                    onClick={clearSimulationFile}
                  >
                    {t("buttons.remove")}
                  </button>
                </div>
              ) : (
                <p className="hb-fin-import__hint">{t("reuse.noFile")}</p>
              )}

              <div className="hb-fin-import__actions">
                <button
                  type="button"
                  className="hb-fin-secondary-btn"
                  onClick={clearSimulationFile}
                  disabled={!dossierFinancialInfo.reuse_simulation_file_name}
                >
                  {t("buttons.clear")}
                </button>

                <button
                  type="button"
                  className="hb-fin-primary-btn"
                  disabled={!dossierFinancialInfo.reuse_simulation_file_name}
                >
                  {t("buttons.uploadApply")}
                </button>
              </div>
            </div>
          </div>
        </div> */}

        <div className="hb-fin-columns">
          <div className="hb-fin-column">
            {renderBorrowerCard(
              t("sections.primaryApplicant"),
              "PRIMARY",
              primaryFinancial,
              "primary",
            )}

            {coBorrower &&
              renderBorrowerCard(
                t("sections.coApplicant"),
                "CO_APPLICANT",
                coFinancial,
                "coApplicant",
              )}
          </div>

          <div className="hb-fin-column">
            <div className="hb-fin-card">
              <h2 className="hb-fin-card__title">{t("sections.loan")}</h2>

              <div className="hb-fin-field hb-fin-field--with-tooltip">
                {renderLabel(
                  t("fields.total_loan_amount"),
                  "total_loan_amount",
                  "dossier",
                )}
                <div className="hb-fin-loan-summary-box">
                  <div className="hb-fin-loan-summary-box__left">
                    <span className="hb-fin-loan-summary-box__value">
                      {totalLoanAmount
                        ? `€ ${Number(totalLoanAmount).toLocaleString("en-US")}`
                        : "-"}
                    </span>
                    <span className="hb-fin-loan-summary-box__label">
                      Total
                    </span>
                  </div>
                  <span className="hb-fin-loan-summary-box__badge">Auto</span>
                </div>
              </div>

              <div className="hb-fin-form-grid">
                <div className="hb-fin-field hb-fin-field--with-tooltip">
                  {renderLabel(
                    t("fields.property_price"),
                    "property_price",
                    "dossier",
                  )}
                  <div className="hb-fin-money-input">
                    <input
                      name="property_price"
                      value={formatThousands(
                        dossierFinancialInfo.property_price,
                      )}
                      onChange={handleDossierFinancialChange}
                      placeholder={t("placeholders.property_price")}
                      inputMode="numeric"
                    />
                    <span className="hb-fin-money-suffix">€</span>
                  </div>
                  {renderError("property_price")}
                </div>

                <div className="hb-fin-field hb-fin-field--with-tooltip">
                  {renderLabel(
                    t("fields.equity_amount"),
                    "equity_amount",
                    "dossier",
                  )}
                  <div className="hb-fin-money-input">
                    <input
                      name="equity_amount"
                      value={formatThousands(
                        dossierFinancialInfo.equity_amount,
                      )}
                      onChange={handleDossierFinancialChange}
                      placeholder={t("placeholders.equity_amount")}
                      inputMode="numeric"
                    />
                    <span className="hb-fin-money-suffix">€</span>
                  </div>
                  {renderError("equity_amount")}
                </div>

                <div className="hb-fin-field hb-fin-field--full">
                  {renderLabel(
                    t("fields.renovation_loan_amount"),
                    "renovation_loan_amount",
                    "dossier",
                  )}

                  <div className="hb-fin-renovation-info">
                    <div className="hb-fin-renovation-info__header">
                      <span className="hb-fin-renovation-info__title">
                        {renovationLoanAmount
                          ? `€ ${Number(renovationLoanAmount).toLocaleString("en-US")}`
                          : t("fields.estimated_after_property_information")}
                      </span>

                      <span className="hb-fin-loan-summary-box__badge">
                        {dossierFinancialInfo.renovation_estimate_source ===
                        "PROPERTY_INFORMATION"
                          ? t("common.auto")
                          : t("common.manual", "Manual")}
                      </span>
                    </div>

                    {dossierFinancialInfo.estimated_renovation_budget ? (
                      <>
                        <p className="hb-fin-renovation-info__text">
                          {t("hints.renovation_estimate_method")}
                        </p>

                        <div className="hb-fin-renovation-info__grid">
                          <div className="hb-fin-renovation-info__metric hb-fin-renovation-info__metric--main">
                            <span className="hb-fin-renovation-info__metric-label">
                              {t("fields.estimated_renovation_budget")}
                            </span>
                            <span className="hb-fin-renovation-info__metric-value">
                              €{" "}
                              {Number(
                                dossierFinancialInfo.estimated_renovation_budget,
                              ).toLocaleString("en-US")}
                            </span>
                          </div>

                          {dossierFinancialInfo.epc_points_reduced ? (
                            <div className="hb-fin-renovation-info__metric">
                              <span className="hb-fin-renovation-info__metric-label">
                                {t("fields.epc_points_reduced")}
                              </span>
                              <span className="hb-fin-renovation-info__metric-value">
                                {dossierFinancialInfo.epc_points_reduced}
                              </span>
                            </div>
                          ) : null}

                          {dossierFinancialInfo.energy_improvement_percent ? (
                            <div className="hb-fin-renovation-info__metric">
                              <span className="hb-fin-renovation-info__metric-label">
                                {t("fields.energy_improvement_percent")}
                              </span>
                              <span className="hb-fin-renovation-info__metric-value">
                                {Number(
                                  dossierFinancialInfo.energy_improvement_percent,
                                ).toFixed(1)}
                                %
                              </span>
                            </div>
                          ) : null}

                          {dossierFinancialInfo.estimated_energy_only_budget ? (
                            <div className="hb-fin-renovation-info__metric">
                              <span className="hb-fin-renovation-info__metric-label">
                                {t("fields.estimated_energy_only_budget")}
                              </span>
                              <span className="hb-fin-renovation-info__metric-value">
                                €{" "}
                                {Number(
                                  dossierFinancialInfo.estimated_energy_only_budget,
                                ).toLocaleString("en-US")}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="hb-fin-renovation-info__footer">
                          <small>
                            {t("hints.renovation_estimate_disclaimer")}
                          </small>
                        </div>
                      </>
                    ) : (
                      <div className="hb-fin-renovation-info__empty">
                        {t("hints.renovation_estimated_later")}
                      </div>
                    )}
                  </div>
                </div>

                <div className="hb-fin-field hb-fin-field--full hb-fin-range-field">
                  {renderLabel(
                    t("fields.tenure_years"),
                    "tenure_years",
                    "dossier",
                  )}
                  <input
                    type="range"
                    min="5"
                    max="30"
                    step="1"
                    value={dossierFinancialInfo.tenure_years}
                    onChange={(e) =>
                      handleRangeChange("tenure_years", e.target.value)
                    }
                    className="hb-fin-range"
                  />
                  <div className="hb-fin-range-meta">
                    <span>5</span>
                    <span>
                      {t("ranges.tenureValue", {
                        value: dossierFinancialInfo.tenure_years,
                      })}
                    </span>
                    <span>30</span>
                  </div>
                  {renderError("tenure_years")}
                </div>

                <div className="hb-fin-field hb-fin-field--full">
                  {renderLabel(
                    t("fields.monthly_cost_percent"),
                    "monthly_cost_percent",
                    "dossier",
                  )}

                  <div className="hb-fin-calculated">
                    <div className="hb-fin-calculated__main">
                      <span className="hb-fin-calculated__percent">
                        {monthlyCostPercent ?? "-"}%
                      </span>

                      <span className="hb-fin-calculated__meta">
                        {t("fields.calculated_automatically")}
                      </span>
                    </div>

                    <div className="hb-fin-calculated__secondary">
                      {estimatedMonthlyPayment
                        ? `≈ €${Math.round(
                            estimatedMonthlyPayment,
                          ).toLocaleString()} / month`
                        : "-"}
                    </div>
                  </div>
                  {renderError("monthly_cost_percent")}
                </div>

                <div className="hb-fin-field hb-fin-field--full">
                  <div className="hb-fin-indicative-note">
                    {t(
                      "hints.indicative_financing_figures",
                      "These figures are indicative and are intended to support discussion with your lender. The platform does not approve, reject, compare, or submit financing requests.",
                    )}
                  </div>
                </div>

                <div className="hb-fin-field hb-fin-field--full hb-fin-field--with-tooltip">
                  {renderLabel(t("fields.notes"), "notes", "dossier")}
                  <textarea
                    name="notes"
                    value={dossierFinancialInfo.notes}
                    onChange={handleDossierFinancialChange}
                    rows={4}
                    placeholder={t("placeholders.notes")}
                  />
                </div>
              </div>
            </div>

            {hasCoApplicant && (
              <div className="hb-fin-card hb-fin-card--compliance-inline">
                <h2 className="hb-fin-card__title">
                  {t("sections.compliance")}
                </h2>

                <div className="hb-fin-form-grid hb-fin-form-grid--compliance-inline">
                  {/* <div className="hb-fin-field hb-fin-field--with-tooltip hb-fin-field--tall3-label">
                    {renderLabel(
                      t("fields.notary_of_choice"),
                      "notary_of_choice",
                      "dossier",
                    )}
                    <select
                      name="notary_of_choice"
                      value={dossierFinancialInfo.notary_of_choice}
                      onChange={handleDossierFinancialChange}
                    >
                      <option value="">{t("placeholders.select")}</option>
                      <option value="NOTARY_1">
                        {t("options.notary.default1")}
                      </option>
                      <option value="NOTARY_2">
                        {t("options.notary.default2")}
                      </option>
                      <option value="NOTARY_3">
                        {t("options.notary.default3")}
                      </option>
                    </select>
                    {renderError("notary_of_choice")}
                  </div> */}

                  <div className="hb-fin-field hb-fin-field--with-tooltip">
                    {renderLabel(
                      t("fields.repayment_bank_account"),
                      "repayment_bank_account",
                      "dossier",
                    )}
                    <input
                      name="repayment_bank_account"
                      value={formatIBAN(
                        dossierFinancialInfo.repayment_bank_account,
                      )}
                      onChange={(e) => {
                        const raw = e.target.value
                          .replace(/\s+/g, "")
                          .toUpperCase();

                        setDossierFinancialInfo((prev) => ({
                          ...prev,
                          repayment_bank_account: raw,
                        }));
                      }}
                      placeholder={t("placeholders.repayment_bank_account")}
                    />
                    {renderError("repayment_bank_account")}
                  </div>

                  <div className="hb-fin-field hb-fin-field--with-tooltip">
                    {renderLabel(
                      t("fields.loan_disbursement_bank_account"),
                      "loan_disbursement_bank_account",
                      "dossier",
                    )}
                    <input
                      name="loan_disbursement_bank_account"
                      value={formatIBAN(
                        dossierFinancialInfo.loan_disbursement_bank_account,
                      )}
                      onChange={(e) => {
                        const raw = e.target.value
                          .replace(/\s+/g, "")
                          .toUpperCase();

                        setDossierFinancialInfo((prev) => ({
                          ...prev,
                          loan_disbursement_bank_account: raw,
                        }));
                      }}
                      placeholder={t(
                        "placeholders.loan_disbursement_bank_account",
                      )}
                    />
                    {renderError("loan_disbursement_bank_account")}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
